<template>
    <div v-else class="app flex-row align-items-center">
        <div class="container">
            <div class="row justify-content-center">
                <spinner v-if="!invitationKey"></spinner>
                <div v-else class="col-9">
                    <div class="card-group mb-0">
                        <div class="card p-4">
                            <div class="card-body login-container">
                                <form @submit.prevent.stop="register" v-debounce="'register'" ref="form">
                                    <p class="font-weight-bold mb-4">{{ trans('contacts.actions.register_for_jibit') }}</p>

                                    <div class="form-group row" v-if="!state.isUser">
                                        <div class="col-12 col-md-4 col-lg-3">
                                            <label for="first_name" class="col-form-label required">{{ trans('contacts.attributes.first_name') }}</label>
                                        </div>
                                        <div class="col-12 col-md-8 col-lg-9">
                                            <input type="text" id="first_name" v-model="user.first_name" class="form-control">
                                        </div>
                                    </div>

                                    <div class="form-group row" v-if="!state.isUser">
                                        <div class="col-12 col-md-4 col-lg-3">
                                            <label for="name" class="col-form-label required">{{ trans('contacts.attributes.surname') }}</label>
                                        </div>
                                        <div class="col-12 col-md-8 col-lg-9">
                                            <input type="text" id="name" v-model="user.name" class="form-control">
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <div class="col-12 col-md-4 col-lg-3">
                                            <label for="email" class="col-form-label required">{{ trans('contacts.attributes.email_address') }} / {{ trans('contacts.attributes.user_name') }}</label>
                                        </div>
                                        <div class="col-12 col-md-8 col-lg-9">
                                            <input type="text" id="email" v-model="user.email" class="form-control" :disabled="state.isUser ? 'disabled' : false" :readonly="state.isUser ? 'readonly' : false">
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <div class="col-12 col-md-4 col-lg-3">
                                            <label for="password" class="col-form-label required">{{ trans('contacts.attributes.password') }}</label>
                                        </div>
                                        <div class="col-12 col-md-8 col-lg-9">
                                            <input type="password" id="password" v-model="user.password" class="form-control">
                                            <div class="help-block"><small>{{ trans('auth.hint_password') }}</small></div>
                                        </div>
                                    </div>

                                    <div class="form-group row">
                                        <div class="col-12 col-md-4 col-lg-3">
                                            <label for="password_repetition" class="col-form-label required">{{ trans('contacts.attributes.password_repetition') }}</label>
                                        </div>
                                        <div class="col-12 col-md-8 col-lg-9">
                                            <input type="password" id="password_repetition" v-model="user.password_repetition" class="form-control">
                                        </div>
                                    </div>

                                    <div class="form-group row has-danger" v-if="hasErrorMessages">
                                        <div class="col-12">
                                            <div class="form-control-feedback" v-html="formattedErrorMessage"></div>
                                        </div>
                                    </div>
                                    <div class="form-group row has-danger" v-if="hasValidationMessages">
                                        <div class="col-12">
                                            <div class="form-control-feedback" v-html="formattedValidationMessage"></div>
                                        </div>
                                    </div>
                                    <spinner v-if="state.loading" class="mt-4"></spinner>
                                    <div class="mt-4">
                                        <button type="submit" class="btn btn-block btn-primary px-4">{{ trans('contacts.actions.register') }}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import { mapState, mapActions } from 'vuex';

    import Spinner from '@/components/Loaders/Spinner';
    import FormGroup from '@/components/Forms/FormGroupHorizontal';

    import RegisterController from '@/services/Auth/RegisterController';
    import LoginController from '@/services/Auth/LoginController';

    export default {
        name: 'auth-register',

        components: {Spinner, FormGroup},

        data() {
            return {
                state: {
                    loading: false,
                    isUser: false
                },
                user: {
                    first_name: null,
                    name: null,
                    user_name: null,
                    email: null,
                    password: null,
                    password_repetition: null
                },
                invitationKey: null
            };
        },

        created() {
            LoginController.authCheck().then(response => {
                if (response) {
                    // Log out lazy testing users who for some reason don't log out before using this
                    this.logout();
                } else {
                    this.showForm();
                }
            });
        },

        mounted() {
            this.setPageTitle(this.trans('contacts.actions.register'));
        },

        computed: {

            ...mapState(['messages']),

            hasValidationMessages() {
                return Object.keys(this.messages.validation).length > 0;
            },
            formattedValidationMessage() {
                return Object.keys(this.messages.validation).reduce((acc, field) => {
                    return `${acc}${acc ? '<br>' : ''}${this.messages.validation[field]}`;
                }, '');
            },

            hasErrorMessages() {
                return this.messages.error.length > 0;
            },
            formattedErrorMessage() {
                return this.messages.error[0];
            }

        },

        methods: {

            ...mapActions(['resetMessages', 'swalToast', 'logoutAuthUser']),

            logout() {
                const self = this;

                this.logoutAuthUser().then(function () {
                    self.showForm();
                });
            },

            showForm() {
                RegisterController.show(
                    this.$router.currentRoute.params.key,
                    this.$router.currentRoute.params.hash
                ).then(response => {
                    if (response.invitationExists) {
                        this.invitationKey = this.$router.currentRoute.params.key;
                        this.state.isUser = response.isUser;

                        if (response.userEmail) {
                            this.user.email = response.userEmail;
                        }
                    } else {
                        this.$router.replace({
                            path: '/login'
                        }, () => {
                            this.swalToast({
                                type: 'error',
                                message: [this.trans('messages.error.registration.link_invalid')]
                            });
                        });
                    }
                });
            },

            register() {
                this.formSubmitShowWait(this.$refs.form);

                this.state.loading = true;

                this.resetMessages();

                const params = Object.assign({}, this.user);

                params.is_user = !!this.state.isUser;

                RegisterController.register(this.invitationKey, params).then(response => {
                    if (response.success) {
                        this.$router.push({
                            name: 'dashboard.index'
                        }, () => {
                            this.swalToast({
                                type: 'success',
                                message: this.trans('messages.success.contacts.registered')
                            });
                        });
                    } else {
                        this.swalToast({
                            type: 'error',
                            message: [this.trans('messages.error.registration.error_occurred')]
                        });
                        this.state.loading = false;
                    }
                }).catch(() => {
                    this.state.loading = false;
                }).finally(() => {
                    this.formSubmitHideWait(this.$refs.form);
                });
            }
        }

    };

</script>



// WEBPACK FOOTER //
// src/views/Auth/Register.vue